import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Group, Stack, Title} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useCallback} from 'react';

import navigateToURL from 'lib/navigateToURL';
import AgentFoundriesList from 'llm/chat-library/foundries/AgentFoundriesList';
import CreateAgentFoundryModal from 'llm/chat-library/foundries/CreateAgentFoundryModal';
import useAgentFoundries from 'llm/chat-library/foundries/useAgentFoundries';
import {agentFoundryPageUrl} from 'llm/routes/UrlUtils';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {CenteredSpinner} from 'ui-library/Spinner';

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
    padding: `${theme.spacing.lg} ${theme.spacing.xxl}`,
    gap: theme.spacing.md,
  },
}));

/**
 * The Agent Foundries list, rendered as a top-level page in the sidepanel's main content slot (the
 * same slot the chat occupies): a title, a simple "Add" button, and a listing. Selecting a row opens
 * the foundry as its own workspace tab (see EditorRoutes.agentFoundryEditor), collapsing this list
 * back to the chat's normal semi-expanded view to make room for it.
 */
export default function AgentFoundriesListPage(): JSX.Element {
  const {agentFoundries, isLoading, isError, refetch, createAgentFoundry} = useAgentFoundries();
  const {classes} = useStyles();
  const [isCreateModalOpen, {open: openCreateModal, close: closeCreateModal}] = useDisclosure(false);

  const handleSelect = useCallback((agentFoundryId: string) => {
    navigateToURL(agentFoundryPageUrl(agentFoundryId));
  }, []);

  return (
    <Stack spacing="md" className={classes.container}>
      <Group noWrap={true} position="apart" align="center">
        <Title order={3}>Foundries</Title>
        <Button
          variant="secondary"
          size="xs"
          leftIcon={<BenchlingIcon icon="plus" />}
          onClick={openCreateModal}
          {...getTestProps({component: 'AgentFoundriesListPage', element: 'addButton'})}
        >
          Add
        </Button>
      </Group>
      {isError ? (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Failed to load foundries."
          action={{
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'Retry',
            actionOnClick: refetch,
          }}
        />
      ) : isLoading ? (
        <CenteredSpinner />
      ) : (
        <AgentFoundriesList agentFoundries={agentFoundries} onSelect={handleSelect} />
      )}
      <CreateAgentFoundryModal
        opened={isCreateModalOpen}
        onRequestClose={closeCreateModal}
        onRequestSubmit={({name, description}) => createAgentFoundry(name, description)}
      />
    </Stack>
  );
}
